import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import { API_ENDPOINT } from "../../config";
import { FulfilmentList, FulfilmentType, SKUItemV2 } from "../../interfaces";
import { PaginationDto } from "../../interfaces/dto/default.dto";

export type FulfilmentPaymentsParams = PaginationDto & {
  cart_order_id?: string;
  sku_code?: string;
  status?: FulfilmentType;
  batch_id?: string;
  shipment_status?: string;
  buyer_name?: string;
  buyer_email?: string;
  registration_number?: string;
};

export type FulfilmentOrderHeader = {
  id: string;
  receipt_no: string | null;
  payment_date: string | null;
  student: {
    name: string | null;
    email: string | null;
    registration_number: string | null;
  };
};

export type FulfilmentPaymentsResponse = {
  entries: SKUItemV2[];
  total: number;
  fulfilled: number;
  unfulfilled: number;
  manifested: number;
  order_created: number;
  order?: FulfilmentOrderHeader | null;
};

export type FulfilmentPickupRow = {
  id: string;
  shipment_id: string | null;
  manifest_id: string | null;
  manifest_created_at: string | null;
  sku_code: string;
  carrier: string;
  awb_number: string | null;
  delivery_partner: string | null;
  picked_up_at: string | null;
  source: string;
  recorded_by_admin_id: string | null;
  created_at: string | null;
  sku_item_status: FulfilmentType | null;
  is_postal_fee_created?: boolean;
  cart_order_id?: string | null;
  quantity: number;
  inventory_name: string | null;
  student: {
    name: string | null;
    email: string | null;
    registration_number: string | null;
  };
  batch: {
    name: string | null;
    course_code: string | null;
  };
  detail?: SKUItemV2;
  details?: SKUItemV2[];
};

export type FulfilmentPickupsParams = PaginationDto & {
  sku_code?: string;
  batch_id?: string;
  buyer_name?: string;
  buyer_email?: string;
  registration_number?: string;
};

export type FulfilmentPickupsResponse = {
  entries: FulfilmentPickupRow[];
  total: number;
};

export type UpdateFulfilmentPayload = {
  status: FulfilmentType;
  tracking_id?: string | null;
  dispatch_date?: string | null;
  delivery_partner?: string | null;
  revert_postal_fee?: boolean;
  /** Reset every SKU on the booked Shipment. Required to undo a booked box. */
  cancel_package?: boolean;
};

export type PostalFeeCarrier = "DELHIVERY" | "EKART" | "MANUAL";

export type PostalFeeRow = {
  id: string;
  pickup_id: string | null;
  carrier: PostalFeeCarrier;
  awb_number: string | null;
  quantity: number;
  weight_gm: number;
  delivery_pincode: string | null;
  delivered_at: string | null;
  freight: number | null;
  gst: number | null;
  gst_rate: number;
  amount: number | null;
  quote_error: string | null;
  student_id: string | null;
  batch_id: string | null;
  student: {
    name: string | null;
    email: string | null;
    registration_number: string | null;
  };
  batch: {
    id: string | null;
    name: string | null;
  };
  inventory: {
    name: string | null;
    sku_code: string | null;
  };
};

export type PostalFeesParams = {
  skip?: number;
  take?: number;
  sort?: string;
  orderby?: string;
  carrier?: PostalFeeCarrier;
  sku_code?: string;
  buyer_name?: string;
  buyer_email?: string;
  registration_number?: string;
  cart_order_ids?: string;
};

export type PostalFeesResponse = {
  entries: PostalFeeRow[];
  total: number;
  gst_rate: number;
};

export const fulfilmentApi = createApi({
  reducerPath: "fulfilmentApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_ENDPOINT + "/admin/fulfilment",
    credentials: "include",
  }),
  tagTypes: ["FulfilmentSummary", "FulfilmentPayments", "FulfilmentPickups", "PostalFees"],
  endpoints: (build) => ({
    getFulfilments: build.query<
      {
        entries: FulfilmentList[];
        total: number;
        unfulfilled: number;
        manifested: number;
        order_created: number;
        fulfilled: number;
      },
      PaginationDto & {
        search?: string;
        status?: FulfilmentType;
        payment_date_from?: string;
        payment_date_to?: string;
      }
    >({
      query: (params) => ({
        url: "/",
        method: "GET",
        params,
      }),
      providesTags: ["FulfilmentSummary"],
    }),

    getFulfilmentPayments: build.query<FulfilmentPaymentsResponse, FulfilmentPaymentsParams>({
      query: (params) => ({
        url: "/payments",
        method: "GET",
        params,
      }),
      providesTags: ["FulfilmentPayments"],
    }),

    getFulfilmentPickups: build.query<FulfilmentPickupsResponse, FulfilmentPickupsParams>({
      query: (params) => ({
        url: "/pickups",
        method: "GET",
        params,
      }),
      providesTags: ["FulfilmentPickups"],
    }),

    updatePickupPostalFeeStatus: build.mutation<
      { updated: number },
      {
        pickup_ids: string[];
        sku_item_ids?: string[];
        is_postal_fee_created: boolean;
      }
    >({
      query: (body) => ({
        url: "/pickups/postal-fee-status",
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        "FulfilmentPickups",
        "FulfilmentSummary",
        "FulfilmentPayments",
        "PostalFees",
      ],
    }),

    getPostalFees: build.query<PostalFeesResponse, PostalFeesParams>({
      query: (params) => ({
        url: "/postal-fees",
        method: "GET",
        params,
      }),
      providesTags: ["PostalFees"],
    }),

    updateFulfilment: build.mutation<void, { id: string; body: UpdateFulfilmentPayload }>({
      query: ({ id, body }) => ({
        url: `/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["FulfilmentPayments", "FulfilmentSummary", "FulfilmentPickups", "PostalFees"],
    }),
  }),
});

export const {
  useGetFulfilmentsQuery,
  useLazyGetFulfilmentsQuery,
  useGetFulfilmentPaymentsQuery,
  useLazyGetFulfilmentPaymentsQuery,
  useGetFulfilmentPickupsQuery,
  useUpdatePickupPostalFeeStatusMutation,
  useGetPostalFeesQuery,
  useUpdateFulfilmentMutation,
} = fulfilmentApi;
